import { CustomImage } from '@app/components/various/CustomImage';
import { Tooltip } from '@app/components/various/Tooltip';
import { getDefaultImageSize } from '@app/ducks';
import { getImageAspectRatio, getProductImageOrPlaceholderBySize } from '@app/ducks/helpers';
import { useIsOverflowing } from '@app/utils/hooks/useIsOverflowing';
import { isDefined } from '@app/utils/is';
import { Product } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

interface Props {
  product?: Product.Standard;
  productId?: string;
}

/**
 * This component displays a thumbnail image and basic information about a product.
 * If the product is not defined, it displays the product ID instead.
 */
export const ProductThumbnail = ({ product, productId }: Props) => {
  const { t } = useTranslation(['common', 'products']);
  const imageAspectRatio = useSelector(getImageAspectRatio);
  const imageSize = useSelector(getDefaultImageSize);
  const image = isDefined(product) ? getProductImageOrPlaceholderBySize(product, imageSize) : undefined;

  const nameRef = React.useRef<HTMLDivElement>(null);
  const isOverflowing = useIsOverflowing(nameRef);

  return (
    <div className="flex items-center gap-3">
      <div className="w-12 flex-shrink-0 overflow-hidden rounded-small">
        <CustomImage src={image?.src} aspectRatio={imageAspectRatio} />
      </div>
      <div className="overflow-hidden">
        <Tooltip content={isOverflowing && (product?.name ?? t('products:product_id'))}>
          <div ref={nameRef} className="line-clamp-2 body-4">
            {product?.name ?? t('products:product_id')}
          </div>
        </Tooltip>
        <div className="text-neutral-60 body-4">{isDefined(product?.sku) ? t('common:art_no', { artNo: product.sku }) : productId}</div>
      </div>
    </div>
  );
};
